import { ScrollArea } from '@/components/ui/scroll-area';
import { useSidebar } from '@/components/ui/sidebar';
import { useRegionsTable } from '@/features/teacher-student/hooks';
import { cn } from '@/lib/utils';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TableSkeleton } from '../../../table-skeleton/table-skeleton';

export const TableDistrict: FC = () => {
  const { open } = useSidebar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    columns,
    masterApprenticesRegions,
    isMasterApprenticesRegionLoading,
    totals,
  } = useRegionsTable();



  const hasData =
    masterApprenticesRegions && masterApprenticesRegions?.length > 0;

  return (
    <div className="w-full rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
      <ScrollArea
        className={cn(open ? 'w-[calc(100vw-320px)]' : 'w-[calc(100vw-150px)]')}
      >
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    'px-6 py-4 text-sm font-semibold text-gray-700',
                    col.align === 'center' && 'text-center',
                    col.align === 'right' && 'text-right',
                    col.align === 'left' && 'text-left',
                  )}
                >
                  {col.header || col.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {isMasterApprenticesRegionLoading ? (
              <TableSkeleton columns={columns.length} />
            ) : hasData ? (
              masterApprenticesRegions!.map((row, rowIndex) => (
                <tr
                  key={row.district_id}
                  className="hover:bg-blue-50/50 transition-colors duration-150"
                >
                  {columns.map((col) => {
                    const value = col.cell
                      ? col.cell(row, rowIndex)
                      : row[col.key as keyof typeof row];

                    const isDistrictName = col.key === 'district_name';

                    return (
                      <td
                        key={col.key}
                        className={cn(
                          'px-6 py-4 text-sm text-gray-800',
                          col.align === 'center' && 'text-center',
                          col.align === 'right' && 'text-right',
                          col.align === 'left' && 'text-left',
                          isDistrictName &&
                          'font-semibold text-dark-blue cursor-pointer hover:underline',
                        )}
                        onClick={() =>
                          isDistrictName && navigate(`${row.district_id}`)
                        }
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length}>
                  <div className="flex h-[260px] flex-col items-center justify-center gap-3 text-gray-500">
                    <svg
                      className="w-16 h-16 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                      />
                    </svg>
                    <p className="text-sm font-medium text-gray-600">
                      {t('nothing.found')}
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
          {hasData && !isMasterApprenticesRegionLoading && (
            <tfoot>
              <tr className="border-t-2">
                {columns.map((col, index) => {
                  const isFirstColumn = index === 0;
                  const isSecondColumn = index === 1;
                  const totalKey = col.key as keyof typeof totals;
                  const totalValue = totals[totalKey];

                  return (
                    <td
                      key={col.key}
                      className={cn(
                        'px-6 py-4 text-sm font-bold',
                        col.align === 'center' && 'text-center',
                        col.align === 'right' && 'text-right',
                        col.align === 'left' && 'text-left',
                      )}
                    >
                      {isFirstColumn ? (
                        ''
                      ) : isSecondColumn ? (
                        <span className="flex items-center gap-2">
                          {t("total")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center px-3 py-1">
                          {totalValue?.toLocaleString() ?? 0}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </ScrollArea>
    </div>
  );
};
